import { css } from '@emotion/core';
import styled from '@emotion/styled';
import {
  faMapMarkedAlt,
  faMoneyBillAlt,
  faPlusCircle,
  faUser,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { alignIconToText, Box, Heading3, Heading4, StackView, Text } from 'localmed-core';
import React from 'react';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import { Link } from '../../../shared/components/DiComponents';
import { DATE_FORMAT, TIME_FORMAT } from '../../../shared/constants';
import { formatStreetAddress } from '../../../shared/utils/formatAddress';
import { formatDate } from '../../../shared/utils/formatCalendarTime';
import getTimeZoneFormat from '../../../shared/utils/getTimeZoneFormat';
import { buildOfficeDirectionsUrl } from '../../../shared/utils/urls';
import type {
  IInsuranceIssuer,
  ILMOffice,
  ILMProvider,
  IOpening,
  IPatient,
  ThemeProps,
} from '../../../types';
import { PROVIDER_AVATAR } from '../../pages/constants';

export const CardWrapper = styled(Box)(
  ({ theme }: ThemeProps) => css`
    background-color: ${theme.colors.inputBackground};
    border-radius: ${theme.radii.xl};
    font-size: ${theme.fontSizes.sm};
    margin: 0 auto;
    width: 100%;
  `
);

export const CardHeader = styled(Box)(
  ({ theme }: ThemeProps) => css`
    padding: ${theme.space.md};
    border-radius: ${theme.radii.xl} ${theme.radii.xl} 0 0;
    background-color: ${theme.colors.blue};
    display: flex;
    justify-content: space-between;
  `,
  alignIconToText
);

export const Avatar = styled('img')`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
`;
export const AvatarContainer = styled('div')(
  ({ theme }: ThemeProps) => css`
    min-width: ${theme.space.lg};
    display: flex;
    justify-content: center;
    margin-right: ${theme.space.sm};
  `
);

export const CardRowLeft = styled(Box)(
  css`
    display: flex;
    align-items: center;
  `
);
export const CardRow = styled(Box)(
  css`
    display: flex;
    align-items: center;
    justify-content: space-between;
  `
);

interface Props {
  setOpenProviderSelection?: (value: boolean) => void;
  setOpenPaymentSelection?: (value: boolean) => void;
  goBack?: () => void;
  goToTimeSelectionStep?: () => void;
  intl: IntlShape;
  appointment?: any;
  opening?: IOpening;
  office?: ILMOffice;
  provider?: ILMProvider;
  patient?: IPatient;
  contact?: IPatient;
  insuranceIssuer?: IInsuranceIssuer;
  patientName?: string;
  contactName?: string;
  showEditInfo?: boolean;
}

function SchedulingDetailsCard({
  intl,
  setOpenPaymentSelection,
  setOpenProviderSelection,
  goBack,
  goToTimeSelectionStep,
  ...props
}: Props) {
  const {
    opening,
    appointment,
    insuranceIssuer,
    office,
    provider,
    patient,
    patientName,
    contactName,
    showEditInfo,
  }: any = props;

  const insuranceDisplay =
    (insuranceIssuer && insuranceIssuer.display) ||
    patient?.primaryInsuranceName ||
    'I Will Pay Myself';

  const { time } = opening || appointment;

  const timeZone = getTimeZoneFormat(office.timeZoneCode);

  return (
    <CardWrapper>
      <CardHeader textAlign="left">
        <StackView spacing="none">
          <Heading3 color="white" fontSize="md">
            {formatDate(intl, time, timeZone, {
              intl: DATE_FORMAT.LONG,
              fallback: DATE_FORMAT.LONG_FALLBACK,
            })}
          </Heading3>
          <Text color="white">
            {formatDate(intl, time, timeZone, {
              intl: TIME_FORMAT.LONG,
              fallback: TIME_FORMAT.LONG_FALLBACK,
            })}
          </Text>
        </StackView>
        {goToTimeSelectionStep && (
          <Link color="white" onClick={goToTimeSelectionStep}>
            <FormattedMessage
              id="scheduling.SchedulingDetailsStep.timeEditLinkMessage"
              defaultMessage="edit"
            />
          </Link>
        )}
      </CardHeader>
      <StackView spacing="md" padding="md">
        <CardRow>
          <CardRowLeft>
            <AvatarContainer>
              <Avatar
                src={provider.profile?.photo || PROVIDER_AVATAR}
                alt={`Photo of ${provider?.display || 'provider'}`}
              />
            </AvatarContainer>
            <Heading4>{provider?.display || '-'}</Heading4>
          </CardRowLeft>
          {setOpenProviderSelection && (
            <Link
              color="black"
              onClick={() => {
                setOpenProviderSelection(true);
              }}
            >
              <FormattedMessage
                id="scheduling.SchedulingDetailsStep.providerEditLinkMessage"
                defaultMessage="edit"
              />
            </Link>
          )}
        </CardRow>

        <CardRow>
          <CardRowLeft>
            <AvatarContainer>
              <FontAwesomeIcon icon={faUser} />
            </AvatarContainer>
            <Text>{patientName}</Text>
          </CardRowLeft>
          {showEditInfo && (
            <Link color="black" onClick={goBack}>
              <FormattedMessage
                id="scheduling.SchedulingDetailsStep.editContactLink"
                defaultMessage="edit"
              />
            </Link>
          )}
        </CardRow>
        {contactName && (
          <CardRow>
            <CardRowLeft>
              <AvatarContainer>
                <FontAwesomeIcon icon={faPlusCircle} />
              </AvatarContainer>
              <Text>{contactName}</Text>
            </CardRowLeft>
            {showEditInfo && (
              <Link color="black" onClick={goBack}>
                <FormattedMessage
                  id="scheduling.SchedulingDetailsStep.editContactLink"
                  defaultMessage="edit"
                />
              </Link>
            )}
          </CardRow>
        )}
        <CardRow>
          <CardRowLeft>
            <AvatarContainer>
              <FontAwesomeIcon icon={faMapMarkedAlt} />
            </AvatarContainer>
            <Box textAlign="left">
              <Link href={buildOfficeDirectionsUrl(office)} target="_blank">
                <Text>{formatStreetAddress(office)}</Text>
                <Text fontSize="sm">{`${office.city}, ${office.stateCode || office.state} ${
                  office.postalCode
                }`}</Text>
              </Link>
            </Box>
          </CardRowLeft>
        </CardRow>
        {office?.insuranceCoverageEnabled && (
          <CardRow>
            <CardRowLeft>
              <AvatarContainer>
                <FontAwesomeIcon icon={faMoneyBillAlt} />
              </AvatarContainer>
              <Text>{insuranceDisplay}</Text>
            </CardRowLeft>
            {setOpenPaymentSelection && (
              <Link
                color="black"
                onClick={() => {
                  setOpenPaymentSelection(true);
                }}
              >
                <FormattedMessage
                  id="scheduling.SchedulingDetailsStep.paymentEditLinkMessage"
                  defaultMessage="edit"
                />
              </Link>
            )}
          </CardRow>
        )}
      </StackView>
    </CardWrapper>
  );
}

export default injectIntl(SchedulingDetailsCard);
